/**
 * <SecretDetailDrawer> — engineer-tier per-secret view + rotate (#251).
 *
 * Right-anchored drawer that mirrors PodDetailDrawer / WorkloadDrawer in
 * shape but is purpose-built for credential exposure:
 *   - Values masked by default (●●●●●).
 *   - Each "Reveal" click triggers a fresh GET to /secrets/:ns/:name (a
 *     deliberate re-fetch, since each call emits a new audit event).
 *   - Revealed values auto-remask after REVEAL_TIMEOUT_MS so the DOM
 *     never holds them past the operator's actual usage.
 *   - "Rotate" button wired to known patterns; the rotation result
 *     (the only surface where new values cross the wire to the user) is
 *     shown once and re-masked the same way.
 *
 * Audit-trail UX: a banner at the top reminds the operator their actions
 * are audited. Per-key reveal => one audit event each. Rotate => its own
 * audit event with the pattern + keys rotated, no values.
 *
 * Defense-in-depth: every endpoint is gated at Engineer at the BFF and
 * deployment-service mirror. This component is also wrapped at the
 * SecretsPanel entry point.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, RefreshCw, X, AlertTriangle, Copy, Check } from 'lucide-react';
import { clusterApi, type SecretMetadata, type SecretRotationResult } from '@/api/client';

interface Props {
  siteId: string;
  secret: SecretMetadata | null;
  onClose: () => void;
}

const REVEAL_TIMEOUT_MS = 10_000;

// Map Secret types to the rotation patterns that make sense for them.
// v1 supports a single pattern (`kubectl-secret-recreate`); future
// patterns would be added here keyed off type or annotation.
function rotationPatternsFor(type: string): { id: string; label: string; description: string }[] {
  // The kubectl-secret-recreate pattern works on any Opaque-shape secret —
  // i.e. anything where regenerating cryptographic random per key is the
  // correct response. Pull-secret-shaped types (dockerconfigjson, tls)
  // need provider-coordinated rotations and are intentionally not offered
  // here in v1.
  if (type === 'Opaque' || type === '') {
    return [
      {
        id: 'kubectl-secret-recreate',
        label: 'Recreate (random)',
        description: 'Generate fresh cryptographically random values for every key, in place.',
      },
    ];
  }
  return [];
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2_000);
    } catch {
      // clipboard blocked (e.g. iframe) — silent. The user can select+copy.
    }
  }
  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={`Copy ${label}`}
      className="text-silver-muted hover:text-silver-bright transition-colors"
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

interface KeyRowProps {
  siteId: string;
  namespace: string;
  name: string;
  keyName: string;
}

function KeyRow({ siteId, namespace, name, keyName }: KeyRowProps) {
  const [revealedValue, setRevealedValue] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-remask after REVEAL_TIMEOUT_MS so the value doesn't linger in the
  // DOM. We only set the timer when we actually have a value to mask.
  useEffect(() => {
    if (revealedValue === null) return;
    const t = setTimeout(() => setRevealedValue(null), REVEAL_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [revealedValue]);

  async function handleReveal() {
    setRevealing(true);
    setError(null);
    try {
      // Fresh GET — server-side audits this call. We do not cache.
      const detail = await clusterApi.getSecret(siteId, namespace, name);
      const value = detail.data[keyName];
      if (typeof value !== 'string') {
        setError('key missing in secret');
        return;
      }
      setRevealedValue(value);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRevealing(false);
    }
  }

  function handleHide() {
    setRevealedValue(null);
  }

  return (
    <div
      data-testid={`secret-key-row-${keyName}`}
      className="flex items-center gap-3 border-b border-surface-elevated last:border-0 px-4 py-3"
    >
      <div className="flex-1 min-w-0">
        <div className="text-xs font-mono text-silver-bright">{keyName}</div>
        <div className="mt-1 font-mono text-sm break-all">
          {revealedValue !== null ? (
            <span data-testid={`secret-value-${keyName}`} className="text-gold select-all">{revealedValue}</span>
          ) : (
            <span data-testid={`secret-mask-${keyName}`} className="text-silver-muted/60">●●●●●●●●●●</span>
          )}
        </div>
        {error && <div className="text-xs text-status-error mt-1">{error}</div>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {revealedValue !== null && <CopyButton value={revealedValue} label={`value of ${keyName}`} />}
        {revealedValue === null ? (
          <button
            type="button"
            onClick={handleReveal}
            disabled={revealing}
            data-testid={`secret-reveal-${keyName}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-gold/10 text-gold text-xs hover:bg-gold/20 transition-colors disabled:opacity-50"
          >
            <Eye size={12} />
            {revealing ? 'Revealing…' : 'Reveal'}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleHide}
            data-testid={`secret-hide-${keyName}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-surface-elevated text-silver-bright text-xs hover:bg-surface-elevated/70 transition-colors"
          >
            <EyeOff size={12} />
            Hide
          </button>
        )}
      </div>
    </div>
  );
}

interface RotateButtonProps {
  siteId: string;
  namespace: string;
  name: string;
  type: string;
  onRotated: (result: SecretRotationResult) => void;
}

function RotateButton({ siteId, namespace, name, type, onRotated }: RotateButtonProps) {
  const [confirmingPattern, setConfirmingPattern] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ pattern }: { pattern: string }) =>
      clusterApi.rotateSecret(siteId, namespace, name, pattern),
    onSuccess: (result) => {
      setConfirmingPattern(null);
      onRotated(result);
      queryClient.invalidateQueries({ queryKey: ['cluster-secrets', siteId] });
    },
  });

  const patterns = rotationPatternsFor(type);
  if (patterns.length === 0) {
    return (
      <p className="text-xs text-silver-muted px-4 py-2 border-t border-surface-elevated">
        No rotation pattern is registered for type <code className="text-silver-bright">{type}</code>.
        Use kubectl or the relevant Helm chart to rotate this secret manually.
      </p>
    );
  }

  return (
    <div className="border-t border-surface-elevated px-4 py-3 space-y-2">
      <h4 className="text-xs font-medium text-silver-muted uppercase tracking-wider">Rotate</h4>
      {patterns.map((p) => (
        <div key={p.id} className="space-y-1">
          <button
            type="button"
            data-testid={`secret-rotate-${p.id}`}
            onClick={() => setConfirmingPattern(p.id)}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-status-warning/10 text-status-warning text-xs hover:bg-status-warning/20 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={12} />
            {p.label}
          </button>
          <p className="text-xs text-silver-muted">{p.description}</p>
        </div>
      ))}

      {confirmingPattern && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Confirm rotation"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
        >
          <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} className="text-status-warning" />
                <h3 className="text-silver-bright font-semibold">Rotate {name}?</h3>
              </div>
              <button onClick={() => setConfirmingPattern(null)} aria-label="Cancel" className="text-silver-muted hover:text-silver-bright">
                <X size={16} />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm text-silver-muted">
              <p>
                This will <span className="text-status-warning font-medium">replace every value</span> in
                <span className="font-mono text-silver-bright"> {namespace}/{name}</span> using the
                <span className="font-mono text-silver-bright"> {confirmingPattern}</span> pattern.
              </p>
              <p>
                Dependent systems consuming the old values will start to fail until they pick up
                the new ones (Helm release recreate, restart, etc.). The new values are shown once;
                copy them before closing this drawer.
              </p>
              {mutation.isError && (
                <p className="text-status-error text-xs">
                  {mutation.error instanceof Error ? mutation.error.message : String(mutation.error)}
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-surface-elevated">
              <button
                onClick={() => setConfirmingPattern(null)}
                className="px-3 py-1.5 rounded-md text-xs text-silver-muted hover:text-silver-bright transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="secret-rotate-confirm"
                onClick={() => mutation.mutate({ pattern: confirmingPattern })}
                disabled={mutation.isPending}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-status-warning/20 text-status-warning text-xs font-medium hover:bg-status-warning/30 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={12} />
                {mutation.isPending ? 'Rotating…' : 'Rotate now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface RotationResultPanelProps {
  result: SecretRotationResult;
  onDismiss: () => void;
}

function RotationResultPanel({ result, onDismiss }: RotationResultPanelProps) {
  // Each rotated key is its own KeyRow-style entry, but we display the
  // value already (this is the one chance the operator gets) and start a
  // remask timer. The user must explicitly dismiss the panel to clear it.
  const [maskedNow, setMaskedNow] = useState<Record<string, boolean>>({});

  // Auto-remask each value after REVEAL_TIMEOUT_MS — same UX as the per-key
  // reveal flow.
  useEffect(() => {
    const timers = Object.keys(result.newValues).map((k) =>
      setTimeout(() => setMaskedNow((prev) => ({ ...prev, [k]: true })), REVEAL_TIMEOUT_MS),
    );
    return () => { timers.forEach(clearTimeout); };
  }, [result]);

  return (
    <div
      role="alert"
      data-testid="secret-rotation-result"
      className="border-t-2 border-gold bg-surface-deep/50 px-4 py-3 space-y-3"
    >
      <div className="flex items-start justify-between">
        <div>
          <h4 className="text-xs font-medium text-gold uppercase tracking-wider">Rotation complete</h4>
          <p className="text-xs text-silver-muted mt-1">
            New values shown once. Copy them now — they will re-mask in 10s.
            Pattern: <span className="font-mono text-silver-bright">{result.pattern}</span>.
          </p>
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss rotation result"
          className="text-silver-muted hover:text-silver-bright"
        >
          <X size={14} />
        </button>
      </div>
      <div>
        {Object.entries(result.newValues).map(([k, v]) => {
          const masked = maskedNow[k] === true;
          return (
            <div key={k} className="py-2 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono text-silver-bright">{k}</div>
                <div className="font-mono text-sm break-all mt-1">
                  {masked ? (
                    <span className="text-silver-muted/60">●●●●●●●●●●</span>
                  ) : (
                    <span className="text-gold select-all" data-testid={`secret-rotation-value-${k}`}>{v}</span>
                  )}
                </div>
              </div>
              {!masked && <CopyButton value={v} label={`new value of ${k}`} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SecretDetailDrawer({ siteId, secret, onClose }: Props) {
  const [rotationResult, setRotationResult] = useState<SecretRotationResult | null>(null);

  // Reset transient state every time we open a different secret.
  useEffect(() => {
    setRotationResult(null);
  }, [secret?.namespace, secret?.name]);

  if (!secret) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Secret detail ${secret.namespace}/${secret.name}`}
      data-testid="secret-detail-drawer"
      className="fixed top-0 right-0 bottom-0 w-full max-w-xl bg-surface border-l border-surface-elevated shadow-2xl z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-surface-elevated">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-silver-bright font-semibold truncate">{secret.name}</h3>
            <span className="text-xs px-2 py-0.5 rounded bg-surface-elevated text-silver-muted">{secret.type}</span>
          </div>
          <div className="text-xs text-silver-muted font-mono mt-0.5">{secret.namespace}</div>
        </div>
        <button
          onClick={onClose}
          aria-label="Close drawer"
          data-testid="secret-detail-drawer-close"
          className="text-silver-muted hover:text-silver-bright transition-colors shrink-0"
        >
          <X size={16} />
        </button>
      </div>

      {/* Audit warning — same content as the panel banner so the operator
          sees it again at the moment of action. */}
      <div className="flex items-start gap-3 bg-status-warning/10 border-b border-status-warning/30 px-6 py-3">
        <AlertTriangle size={14} className="text-status-warning shrink-0 mt-0.5" />
        <p className="text-xs text-silver-bright">
          Every reveal is audited and visible to your organization&apos;s admins.
        </p>
      </div>

      {/* Per-key rows */}
      <div className="flex-1 overflow-y-auto">
        {secret.keys.length === 0 ? (
          <p className="px-6 py-4 text-sm text-silver-muted">This secret has no data keys.</p>
        ) : (
          secret.keys.map((k) => (
            <KeyRow
              key={k}
              siteId={siteId}
              namespace={secret.namespace}
              name={secret.name}
              keyName={k}
            />
          ))
        )}

        {rotationResult && (
          <RotationResultPanel
            result={rotationResult}
            onDismiss={() => setRotationResult(null)}
          />
        )}
      </div>

      {/* Rotate footer */}
      <RotateButton
        siteId={siteId}
        namespace={secret.namespace}
        name={secret.name}
        type={secret.type}
        onRotated={(r) => setRotationResult(r)}
      />
    </div>
  );
}
